// StepWizard.js - Step-by-step wizard interface
import React from 'react';
import { ChevronRight, Check, Circle } from 'lucide-react';

const StepWizard = ({
  currentStep,
  onStepChange,
  steps = [],
  className = ''
}) => {
  const defaultSteps = [
    { id: 0, title: 'Load/Create', description: 'Load existing or create new guide' },
    { id: 1, title: 'Build Guide', description: 'Create your guide content' },
    { id: 2, title: 'Review', description: 'Review and finalize' },
    { id: 3, title: 'Export', description: 'Save and export your guide' }
  ];

  const wizardSteps = steps.length > 0 ? steps : defaultSteps;

  const getStepStatus = (stepIndex) => {
    // Adjust for the fact that we skip step 0 (Welcome) in the progress bar
    const actualStep = stepIndex + 1;
    if (actualStep < currentStep) return 'completed';
    if (actualStep === currentStep) return 'active';
    return 'pending';
  };

  const getStepIcon = (stepIndex) => {
    const status = getStepStatus(stepIndex);

    switch (status) {
      case 'completed':
        return <Check className="w-4 h-4 text-white" />;
      case 'active':
        return <Circle className="w-4 h-4 text-white fill-current" />;
      default:
        return <span className="text-xs font-medium text-gray-500">{stepIndex + 1}</span>;
    }
  };

  const getStepClasses = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    const baseClasses = "flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200";

    switch (status) {
      case 'completed':
        return `${baseClasses} bg-green-500 border-green-500`;
      case 'active':
        return `${baseClasses} bg-blue-500 border-blue-500`;
      default:
        return `${baseClasses} bg-white border-gray-300`;
    }
  };

  const handleStepClick = (stepIndex) => {
    // Adjust for the fact that we skip step 0 (Welcome) in the progress bar
    const actualStep = stepIndex + 1;
    // Only allow navigation to completed steps or the next step
    if (actualStep <= currentStep || actualStep === currentStep + 1) {
      onStepChange(actualStep);
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-2 ${className}`}>
      <h3 className="text-sm font-semibold text-gray-800 mb-2">Progress</h3>

      <div className="flex items-center justify-between">
        {wizardSteps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Step Circle */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => handleStepClick(index)}
                className={`${getStepClasses(index)} ${
                  (index + 1) <= currentStep || (index + 1) === currentStep + 1
                    ? 'cursor-pointer hover:scale-105'
                    : 'cursor-not-allowed'
                }`}
                disabled={(index + 1) > currentStep + 1}
                title={step.description}
              >
                {getStepIcon(index)}
              </button>

              {/* Step Label */}
              <div className="mt-0.5 text-center">
                <p className={`text-sm font-medium ${
                  getStepStatus(index) === 'active'
                    ? 'text-blue-600'
                    : getStepStatus(index) === 'completed'
                    ? 'text-green-600'
                    : 'text-gray-500'
                }`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-400 max-w-[12rem] truncate">
                  {step.description}
                </p>
              </div>
            </div>

            {/* Arrow between steps */}
            {index < wizardSteps.length - 1 && (
              <div className="flex-1 px-4">
                <ChevronRight className={`w-4 h-4 mx-auto ${
                  (index + 1) < currentStep ? 'text-green-500' : 'text-gray-300'
                }`} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div> 
      
    </div>
  );
};

export default StepWizard;